import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Typography, makeStyles, IconButton } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useIntl } from 'react-intl'

import { studentCourseActions } from '../../../_state/actions'
import { CapeeshColors } from '../../../assets/ColorPalette'
import { getFlagAssetForLanguageId } from '../../../Logic/FlagUnicodeLogic'
import TopicCard from './TopicCard'
import TopicDetailView from './TopicDetailView'
import { studentScreenMessages } from '../../../Views/StudentPrivate/StudentScreen.i18n.Messages'

import type {
    CourseGroupV1Dto,
    CourseGroupSubPartDto,
    CourseMetadataV1Dto,
    CourseGroupMetadataV2Dto,
    CourseGroupSupportedLanguageDto,
    CourseGroupProgressionV1Dto,
    CourseGroupSubPartProgressionDto,
    CourseProgressionDto,
    CourseLessonOrderDto,
    CourseLessonOrderProgressionDto,
} from '../../../Logic/StudentCourseDtos'

// ─── Styles ───────────────────────────────────────────────────────

const PANEL_WIDTH = 440

const useStyles = makeStyles((theme) => ({
    backdrop: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
        zIndex: 1200,
        opacity: 0,
        transition: 'opacity 300ms ease-in-out',
        pointerEvents: 'none' as const,
    },
    backdropOpen: {
        opacity: 1,
        pointerEvents: 'auto' as const,
    },
    panel: {
        position: 'fixed' as const,
        top: 0,
        right: 0,
        bottom: 0,
        width: PANEL_WIDTH,
        maxWidth: '100vw',
        backgroundColor: '#FFFFFF',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column' as const,
        boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.12)',
        transform: `translateX(${PANEL_WIDTH}px)`,
        transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        '@media (max-width: 599px)': {
            width: '100vw',
            transform: 'translateX(100vw)',
        },
    },
    panelOpen: {
        transform: 'translateX(0)',
    },
    pageSlider: {
        display: 'flex',
        flexDirection: 'row' as const,
        width: PANEL_WIDTH * 2,
        minHeight: '100%',
        transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        '@media (max-width: 599px)': {
            width: '200vw',
        },
    },
    pageSliderShifted: {
        transform: `translateX(-${PANEL_WIDTH}px)`,
        '@media (max-width: 599px)': {
            transform: 'translateX(-100vw)',
        },
    },
    page: {
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        display: 'flex',
        flexDirection: 'column' as const,
        overflowY: 'auto' as const,
        height: '100vh',
        '@media (max-width: 599px)': {
            width: '100vw',
            minWidth: '100vw',
        },
    },
    panelHeader: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: '24px 20px 0 24px',
        position: 'sticky' as const,
        top: 0,
        backgroundColor: '#FFFFFF',
        zIndex: 1,
        '@media (max-width: 599px)': {
            padding: '16px 12px 0 16px',
        },
    },
    headerTextBlock: {
        flex: 1,
        paddingRight: 8,
    },
    courseGroupName: {
        fontFamily: 'Rubik',
        fontSize: 20,
        fontWeight: 600,
        lineHeight: '130%',
        color: CapeeshColors.DarkText,
    },
    courseGroupDescription: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        lineHeight: '150%',
        color: CapeeshColors.Gray1,
        marginTop: 4,
    },
    closeButton: {
        marginTop: -4,
        marginRight: -4,
        color: CapeeshColors.Gray1,
        '&:hover': {
            color: CapeeshColors.DarkText,
            backgroundColor: CapeeshColors.Gray5,
        },
    },
    panelBody: {
        padding: '16px 24px 32px 24px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 24,
        '@media (max-width: 599px)': {
            padding: '12px 16px 32px 16px',
        },
    },
    subPartSection: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
    },
    subPartHeader: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 600,
        color: CapeeshColors.Primary900,
        lineHeight: '130%',
        paddingBottom: 4,
        borderBottom: `1px solid ${CapeeshColors.Gray4}`,
    },
    topicList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 14,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
        gap: 12,
    },
    loadingText: {
        fontFamily: 'Rubik',
        fontSize: 13,
        color: CapeeshColors.Gray1,
    },
    overallProgressRow: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        gap: 10,
        padding: '12px 24px 0 24px',
        '@media (max-width: 599px)': {
            padding: '12px 16px 0 16px',
        },
    },
    overallProgressBarBg: {
        flex: 1,
        height: 5,
        borderRadius: 3,
        backgroundColor: CapeeshColors.Gray5,
        overflow: 'hidden',
    },
    overallProgressBarFill: {
        height: '100%',
        borderRadius: 3,
        transition: 'width 600ms ease-in-out',
    },
    overallProgressLabel: {
        fontFamily: 'Rubik',
        fontSize: 12,
        fontWeight: 500,
        color: CapeeshColors.Gray1,
        minWidth: 36,
        textAlign: 'right' as const,
    },
    languageSection: {
        padding: '12px 24px 0 24px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 6,
        '@media (max-width: 599px)': {
            padding: '12px 16px 0 16px',
        },
    },
    languageSectionLabel: {
        fontFamily: 'Rubik',
        fontSize: 12,
        fontWeight: 500,
        color: CapeeshColors.Gray1,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
    },
    languageDropdownAnchor: {
        position: 'relative' as const,
    },
    languageDropdownTrigger: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 8,
        border: `1.5px solid ${CapeeshColors.Gray4}`,
        backgroundColor: '#FFFFFF',
        cursor: 'pointer',
        transition: 'border-color 150ms ease',
        userSelect: 'none' as const,
        '&:hover': {
            borderColor: CapeeshColors.Primary600,
        },
    },
    languageDropdownTriggerOpen: {
        borderColor: CapeeshColors.Primary600,
    },
    languageDropdownTriggerDisabled: {
        opacity: 0.5,
        pointerEvents: 'none' as const,
    },
    languageDropdownFlag: {
        width: 22,
        height: 22,
        borderRadius: '50%',
        objectFit: 'cover' as const,
        flexShrink: 0,
    },
    languageDropdownTriggerLabel: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        color: CapeeshColors.DarkText,
        flex: 1,
    },
    languageDropdownArrow: {
        fontSize: 18,
        color: CapeeshColors.Gray1,
        transition: 'transform 200ms ease',
        lineHeight: 1,
    },
    languageDropdownArrowOpen: {
        transform: 'rotate(180deg)',
    },
    languageDropdownList: {
        position: 'absolute' as const,
        top: 'calc(100% + 4px)',
        left: 0,
        right: 0,
        backgroundColor: '#FFFFFF',
        borderRadius: 8,
        border: `1px solid ${CapeeshColors.Gray4}`,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.10)',
        zIndex: 10,
        overflow: 'hidden',
        maxHeight: 220,
        overflowY: 'auto' as const,
    },
    languageDropdownItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px',
        cursor: 'pointer',
        transition: 'background-color 100ms ease',
        '&:hover': {
            backgroundColor: CapeeshColors.Gray5,
        },
    },
    languageDropdownItemSelected: {
        backgroundColor: CapeeshColors.Primary50 || '#F0F4FF',
        '&:hover': {
            backgroundColor: CapeeshColors.Primary50 || '#F0F4FF',
        },
    },
    languageDropdownItemLabel: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        color: CapeeshColors.DarkText,
    },
    languageDropdownItemLabelSelected: {
        fontWeight: 600,
    },
    languageDropdownItemCheck: {
        marginLeft: 'auto',
        fontSize: 16,
        color: CapeeshColors.Primary600,
        lineHeight: 1,
    },
}))

// ─── Types ────────────────────────────────────────────────────────

interface CourseDetailPanelProps {
    open: boolean
    courseGroup: CourseGroupV1Dto | null
    progression: CourseGroupProgressionV1Dto | null
    metadata: CourseGroupMetadataV2Dto | null
    settingL1: boolean
    courseLessonOrder: CourseLessonOrderDto | null
    courseLessonOrderProgression: CourseLessonOrderProgressionDto | null
    fetchingCourseLessonOrder: boolean
    fetchingCourseLessonOrderProgression: boolean
    restoredSelectedTopicId: number | null
    dispatch: (action: any) => void
    onClose: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────

/**
 * Build a lookup from topicId -> CourseProgressionDto using the sub-part progressions.
 */
function buildTopicProgressionMap(
    progression: CourseGroupProgressionV1Dto | null
): Record<number, CourseProgressionDto> {
    const map: Record<number, CourseProgressionDto> = {}
    if (!progression?.courseGroupSubPartProgressions) {
        return map
    }
    progression.courseGroupSubPartProgressions.forEach((subPartProg: CourseGroupSubPartProgressionDto) => {
        if (!subPartProg?.courseProgressions) return
        subPartProg.courseProgressions.forEach((cp: CourseProgressionDto) => {
            if (cp && cp.topicId != null) {
                map[cp.topicId] = cp
            }
        })
    })
    return map
}

// ─── Component ────────────────────────────────────────────────────

const CourseDetailPanel: React.FC<CourseDetailPanelProps> = ({
    open,
    courseGroup,
    progression,
    metadata,
    settingL1,
    courseLessonOrder,
    courseLessonOrderProgression,
    fetchingCourseLessonOrder,
    fetchingCourseLessonOrderProgression,
    restoredSelectedTopicId,
    dispatch,
    onClose,
}) => {
    const classes = useStyles()
    const intl = useIntl()

    // ─── Internal topic navigation state ──────────────────────
    const [selectedTopicId, setSelectedTopicId] = useState<number | null>(restoredSelectedTopicId)

    // Sync selectedTopicId to Redux so it's available across navigation
    useEffect(() => {
        dispatch(studentCourseActions.setSelectedTopicId(selectedTopicId))
    }, [selectedTopicId, dispatch])

    // Reset selected topic when panel closes
    useEffect(() => {
        if (!open) {
            const timer = setTimeout(() => setSelectedTopicId(null), 350)
            return () => clearTimeout(timer)
        }
    }, [open])

    // Reset selected topic when the course group changes (but not on initial mount)
    const prevCourseGroupIdRef = useRef(courseGroup?.id)
    useEffect(() => {
        if (prevCourseGroupIdRef.current !== courseGroup?.id) {
            setSelectedTopicId(null)
        }
        prevCourseGroupIdRef.current = courseGroup?.id
    }, [courseGroup?.id])

    // Find the selected topic metadata from the course group
    const selectedTopic: CourseMetadataV1Dto | null = useMemo(() => {
        if (selectedTopicId == null || !courseGroup?.courseGroupSubParts) return null
        for (const subPart of courseGroup.courseGroupSubParts) {
            if (!subPart?.courses) continue
            for (const topic of subPart.courses) {
                if (topic?.topicId === selectedTopicId) return topic
            }
        }
        return null
    }, [selectedTopicId, courseGroup])

    const topicProgressionMap = useMemo(() => {
        return buildTopicProgressionMap(progression)
    }, [progression])

    const subParts = courseGroup?.courseGroupSubParts || []
    const hasSingleSubPart = subParts.length === 1
    const isL2Only = metadata?.l2Only ?? false
    const name = courseGroup
        ? (isL2Only
            ? (courseGroup.l2Name || intl.formatMessage(studentScreenMessages.courseFallback))
            : (courseGroup.l1Name || courseGroup.l2Name || intl.formatMessage(studentScreenMessages.courseFallback)))
        : ''
    const description = courseGroup
        ? (isL2Only
            ? (courseGroup.l2Description || '')
            : (courseGroup.l1Description || courseGroup.l2Description || ''))
        : ''

    const overallProgression = progression?.courseGroupNormalizedProgression ?? null
    const overallPercent = overallProgression != null
        ? Math.min(Math.round(overallProgression * 100), 100)
        : null
    const overallComplete = overallProgression != null && overallProgression >= 0.999

    const handleTopicSelect = useCallback((topicId: number) => {
        setSelectedTopicId(topicId)
    }, [])

    const handleTopicBack = useCallback(() => {
        setSelectedTopicId(null)
    }, [])

    // ─── Language dropdown ───────────────────────────────────
    const [langDropdownOpen, setLangDropdownOpen] = useState(false)
    const langDropdownRef = useRef<HTMLDivElement>(null)

    const supportedLanguages: CourseGroupSupportedLanguageDto[] =
        metadata?.supportedL1Languages && metadata.supportedL1Languages.length > 0
            ? metadata.supportedL1Languages
            : []
    const currentL1Id: number | null = metadata?.l1Id ?? null

    const currentLanguage: CourseGroupSupportedLanguageDto | null = useMemo(() => {
        if (currentL1Id == null) return null
        return supportedLanguages.find((l) => l.l1Id === currentL1Id) || null
    }, [supportedLanguages, currentL1Id])

    const handleLanguageSelect = useCallback((l1Id: number) => {
        if (!metadata?.courseGroupId || l1Id === currentL1Id || settingL1) return
        setLangDropdownOpen(false)
        dispatch(studentCourseActions.setCourseGroupL1(metadata.courseGroupId, l1Id))
    }, [metadata?.courseGroupId, currentL1Id, settingL1, dispatch])

    // Close dropdown on outside click
    useEffect(() => {
        if (!langDropdownOpen) return
        const handleClickOutside = (e: MouseEvent) => {
            if (langDropdownRef.current && !langDropdownRef.current.contains(e.target as Node)) {
                setLangDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [langDropdownOpen])

    // Close dropdown when panel closes
    useEffect(() => {
        if (!open) setLangDropdownOpen(false)
    }, [open])

    const handleBackdropClick = () => {
        if (selectedTopicId != null) {
            setSelectedTopicId(null)
        } else {
            onClose()
        }
    }

    return (
        <>
            {/* Backdrop */}
            <div
                className={`${classes.backdrop} ${open ? classes.backdropOpen : ''}`}
                onClick={handleBackdropClick}
            />

            {/* Slide-in panel */}
            <div className={`${classes.panel} ${open ? classes.panelOpen : ''}`}>
                {courseGroup ? (
                    <div
                        className={`${classes.pageSlider} ${selectedTopicId != null ? classes.pageSliderShifted : ''}`}
                    >
                        {/* Page 1: Topic list */}
                        <div className={classes.page}>
                            {/* Header */}
                            <div className={classes.panelHeader}>
                                <div className={classes.headerTextBlock}>
                                    <Typography className={classes.courseGroupName}>
                                        {name}
                                    </Typography>
                                    {description && (
                                        <Typography className={classes.courseGroupDescription}>
                                            {description}
                                        </Typography>
                                    )}
                                </div>
                                <IconButton
                                    className={classes.closeButton}
                                    onClick={onClose}
                                    size="small"
                                    aria-label={intl.formatMessage(studentScreenMessages.close)}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </div>

                            {/* Language dropdown - hidden for monolingual courses (single language) */}
                            {supportedLanguages.length > 1 && (
                                <div className={classes.languageSection}>
                                    <Typography className={classes.languageSectionLabel}>
                                        {intl.formatMessage(studentScreenMessages.yourLanguage)}
                                    </Typography>
                                    <div className={classes.languageDropdownAnchor} ref={langDropdownRef}>
                                        <div
                                            className={`${classes.languageDropdownTrigger} ${langDropdownOpen ? classes.languageDropdownTriggerOpen : ''} ${settingL1 ? classes.languageDropdownTriggerDisabled : ''}`}
                                            onClick={() => setLangDropdownOpen((prev) => !prev)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') setLangDropdownOpen((prev) => !prev)
                                            }}
                                        >
                                            {currentLanguage && getFlagAssetForLanguageId(currentLanguage.l1Id) && (
                                                <img
                                                    src={getFlagAssetForLanguageId(currentLanguage.l1Id)}
                                                    alt=""
                                                    className={classes.languageDropdownFlag}
                                                />
                                            )}
                                            <span className={classes.languageDropdownTriggerLabel}>
                                                {currentLanguage?.l1NativeFriendlyName || intl.formatMessage(studentScreenMessages.selectLanguage)}
                                            </span>
                                            <span className={`${classes.languageDropdownArrow} ${langDropdownOpen ? classes.languageDropdownArrowOpen : ''}`}>
                                                &#9662;
                                            </span>
                                        </div>

                                        {langDropdownOpen && (
                                            <div className={classes.languageDropdownList}>
                                                {supportedLanguages.map((lang: CourseGroupSupportedLanguageDto) => {
                                                    const isSelected = lang.l1Id === currentL1Id
                                                    const flagSrc = getFlagAssetForLanguageId(lang.l1Id)
                                                    return (
                                                        <div
                                                            key={lang.l1Id}
                                                            className={`${classes.languageDropdownItem} ${isSelected ? classes.languageDropdownItemSelected : ''}`}
                                                            onClick={() => handleLanguageSelect(lang.l1Id)}
                                                            role="option"
                                                            aria-selected={isSelected}
                                                        >
                                                            {flagSrc && (
                                                                <img
                                                                    src={flagSrc}
                                                                    alt=""
                                                                    className={classes.languageDropdownFlag}
                                                                />
                                                            )}
                                                            <span className={`${classes.languageDropdownItemLabel} ${isSelected ? classes.languageDropdownItemLabelSelected : ''}`}>
                                                                {lang.l1NativeFriendlyName}
                                                            </span>
                                                            {isSelected && (
                                                                <span className={classes.languageDropdownItemCheck}>&#10003;</span>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Overall progression bar */}
                            {overallPercent != null && (
                                <div className={classes.overallProgressRow}>
                                    <div className={classes.overallProgressBarBg}>
                                        <div
                                            className={classes.overallProgressBarFill}
                                            style={{
                                                width: `${overallPercent}%`,
                                                backgroundColor: overallComplete
                                                    ? CapeeshColors.GreenBright
                                                    : CapeeshColors.Primary600,
                                            }}
                                        />
                                    </div>
                                    <Typography className={classes.overallProgressLabel}>
                                        {overallPercent}%
                                    </Typography>
                                </div>
                            )}

                            {/* Body: sub-parts and topic cards */}
                            <div className={classes.panelBody}>
                                {subParts.map((subPart: CourseGroupSubPartDto, spIdx: number) => {
                                    const topics = subPart?.courses || []
                                    if (topics.length === 0) return null

                                    return (
                                        <div key={subPart.id || spIdx} className={classes.subPartSection}>
                                            {!hasSingleSubPart && (
                                                <Typography className={classes.subPartHeader}>
                                                    {isL2Only
                                                        ? (subPart.l2Name || intl.formatMessage(studentScreenMessages.partNumber, { number: spIdx + 1 }))
                                                        : (subPart.l1Name || subPart.l2Name || intl.formatMessage(studentScreenMessages.partNumber, { number: spIdx + 1 }))}
                                                </Typography>
                                            )}

                                            <div className={classes.topicList}>
                                                {topics.map((topic: CourseMetadataV1Dto, tIdx: number) => {
                                                    const topicProgression =
                                                        topic?.topicId != null
                                                            ? topicProgressionMap[topic.topicId] || null
                                                            : null

                                                    return (
                                                        <TopicCard
                                                            key={topic.topicId ?? tIdx}
                                                            topic={topic}
                                                            progression={topicProgression}
                                                            onSelect={handleTopicSelect}
                                                            index={tIdx}
                                                        />
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        {/* Page 2: Topic detail (lessons) */}
                        <div className={classes.page}>
                            {selectedTopic ? (
                                <TopicDetailView
                                    topic={selectedTopic}
                                    courseGroupId={courseGroup.id}
                                    l1Id={courseGroup.l1Id}
                                    lessonOrder={courseLessonOrder}
                                    lessonOrderProgression={courseLessonOrderProgression}
                                    fetchingLessonOrder={fetchingCourseLessonOrder}
                                    fetchingLessonOrderProgression={fetchingCourseLessonOrderProgression}
                                    dispatch={dispatch}
                                    onBack={handleTopicBack}
                                    isNativeSpeaker={metadata?.nativeSpeakerEnabled && isL2Only}
                                />
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={32} style={{ color: CapeeshColors.Primary600 }} />
                        <Typography className={classes.loadingText}>
                            {intl.formatMessage(studentScreenMessages.loadingCourseDetails)}
                        </Typography>
                    </div>
                )}
            </div>
        </>
    )
}

export default CourseDetailPanel
